import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material'
import MuiAccordion from '@mui/material/Accordion'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import { styled } from '@mui/material/styles'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

// Icons
import ArrowIconRight from '../../../Assets/Icons/Arrow_Small_Right.svg'
import PDFIcon from '../../../Assets/Icons/PDF_Icon.svg'
import XLSXIcon from '../../../Assets/Icons/XL_Icon.svg'
import DOCIcon from '../../../Assets/Icons/Doc_Icon.svg'
import UnknownFile from '../../../Assets/Icons/document_icon.svg'

// File Type Icons
const FILE_ICONS = {
  pdf: PDFIcon,
  xlsx: XLSXIcon,
  doc: DOCIcon,
  docx: DOCIcon,
  default: UnknownFile,
}

// Styled Components - Updated with better UI
const StyledAccordion = styled(MuiAccordion)(({ theme }) => ({
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: '8px !important',
  marginBottom: theme.spacing(1),
  transition: 'all 0.2s ease',
  overflow: 'hidden',
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    margin: theme.spacing(1.5, 0),
    boxShadow: theme.shadows[2],
    backgroundColor: '#f1521536',
  },
}))

const StyledAccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  flexDirection: 'row-reverse',
  minHeight: '56px',
  '& .MuiAccordionSummary-content': {
    margin: theme.spacing(0.5, 0),
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: 'rotate(0deg)',
    transition: 'all 0.3s ease',
    '&.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
  },
  '&.Mui-expanded': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  },
}))

const StyledAccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: 0,
  backgroundColor: theme.palette.background.paper,
  transition: 'background 0.3s ease',
}))

const SubItem = styled(Box)(({ theme, selected }) => ({
  padding: theme.spacing(1.75, 3, 1.75, 4),
  margin: theme.spacing(0.5, 0),
  cursor: 'pointer',
  position: 'relative',
  backgroundColor: selected
    ? alpha(theme.palette.primary.main, 0.08)
    : 'transparent',
  borderLeft: selected
    ? `3px solid ${theme.palette.primary.main}`
    : `3px solid transparent`,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: selected
      ? theme.palette.primary.main
      : alpha(theme.palette.text.secondary, 0.5),
  },
}))

const FileCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  borderRadius: 12,
  padding: theme.spacing(2),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: theme.shadows[1],
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[4],
    borderColor: theme.palette.primary.main,
  },
}))

const DynamicAccordion = ({
  data,
  subData,
  accordionTitleKey = 'Heading',
  accordionSubtitleKey = 'Subheading',
  accordionIdKey = 'ID',
  fileTitleKey = 'Title',
  fileNameKey = 'filename',
  filePathKey = 'pathurl',
  fileIdKey = 'id',
  urlsKey = 'URLs',
  loading = false,
  initialExpanded = 0,
  onItemSelect,
  navpath,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(initialExpanded)
  const [selectedData, setSelectedData] = useState([])
  const [isLoading, setIsLoading] = useState(loading)
  const isMounted = useRef(true)

  const handleChange = (panel) => (_, newExpanded) => {
    setExpanded(newExpanded ? panel : false)
  }

  const filterData = useCallback(
    (selected) => {
      setIsLoading(true)
      setTimeout(() => {
        const filtered = subData.filter(
          (item) => item[accordionIdKey] === selected[accordionIdKey]
        )
        setSelectedData(filtered)
        setIsLoading(false)
        onItemSelect?.(filtered)
      }, 500)
    },
    [subData, accordionIdKey, onItemSelect]
  )

  useEffect(() => {
    if (subData?.length > 0) filterData(subData[0])
    return () => (isMounted.current = false)
  }, [subData, filterData])

  const handleFileClick = (fileData) => {
    if (!fileData[filePathKey]?.trim()) {
      toast.error('File not available')
      return
    }

    onItemSelect?.(fileData) ||
      navigate(`/${navpath}/${fileData[fileIdKey]}/${fileData[fileNameKey]}`, {
        state: { selectedDocData: fileData },
      })
  }

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop().toLowerCase()
    return FILE_ICONS[ext] || FILE_ICONS.default
  }

  const renderFileItem = (fileData, index) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
      <Tooltip title={fileData[fileNameKey]} arrow>
        <FileCard onClick={() => handleFileClick(fileData)}>
          <img
            src={getFileIcon(fileData[fileNameKey])}
            alt="file-icon"
            width={64}
            height={64}
            style={{ marginBottom: 12 }}
          />

          <Typography variant="body2" fontWeight={500} noWrap>
            {fileData[fileNameKey]}
          </Typography>
        </FileCard>
      </Tooltip>
    </Grid>
  )

  const renderContentSection = () => {
    if (isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      )
    }

    if (!selectedData[0]?.[urlsKey]) {
      return (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="textSecondary">
            No documents available
          </Typography>
        </Box>
      )
    }

    return selectedData.map((item) =>
      Object.entries(item[urlsKey]).map(([key, files]) => (
        <Box key={key} mb={4}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {files[0]?.[fileTitleKey] || 'Documents'}
          </Typography>
          <Grid container spacing={2}>
            {files.map(renderFileItem)}
          </Grid>
        </Box>
      ))
    )
  }

  return (
    <Box className="container" py={4}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4} lg={3}>
          {data?.map((item, index) => {
            const isItemExpanded = expanded === index
            const isDisabled = item.isActive === false

            return (
              <StyledAccordion
                key={index}
                expanded={isItemExpanded}
                onChange={handleChange(index)}
                onClick={() =>
                  !isItemExpanded &&
                  item?.SubHeading?.[0] &&
                  filterData(item.SubHeading[0])
                }
                disabled={isDisabled}
                sx={{
                  opacity: isDisabled ? 0.7 : 1,
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.grey[50],
                  },
                }}
              >
                <StyledAccordionSummary
                  expandIcon={<img src={ArrowIconRight} alt=">" />}
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{
                      color: isDisabled
                        ? theme.palette.text.disabled
                        : theme.palette.text.primary,
                    }}
                  >
                    {item[accordionTitleKey]}
                  </Typography>
                </StyledAccordionSummary>

                <StyledAccordionDetails>
                  {item?.SubHeading?.map((subItem, i) => {
                    const isSelected = selectedData.some(
                      (d) => d[accordionIdKey] === subItem[accordionIdKey]
                    )

                    return (
                      <SubItem
                        key={i}
                        selected={isSelected}
                        onClick={() => filterData(subItem)}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{
                            color: isSelected
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                          }}
                        >
                          {subItem[accordionSubtitleKey]}
                        </Typography>
                      </SubItem>
                    )
                  })}
                </StyledAccordionDetails>
              </StyledAccordion>
            )
          })}
        </Grid>

        <Grid item xs={12} md={8} lg={9}>
          {renderContentSection()}
        </Grid>
      </Grid>

      <ToastContainer position="bottom-right" />
    </Box>
  )
}

export default DynamicAccordion
